import { cn } from "@/lib/utils";

/** Split-layout premium auth shell — brand marketing on LEFT, sign-in form on RIGHT.
 * Matches the DAAKYI mockup: dark navy left with topographic contour pattern +
 * warm glow, cream right panel with the form.
 */
export function AuthShell({ leftPanel, testId, children }) {
  return (
    <div className="relative flex min-h-screen w-full flex-col md:flex-row" data-testid={testId}>
      <aside
        className="relative flex flex-col justify-between gap-y-10 overflow-hidden bg-[#0A0F2E] px-8 py-10 text-white md:w-1/2 md:px-14 md:py-14 lg:px-20"
        data-testid={`${testId}-brand`}
      >
        {leftPanel}
      </aside>
      <main
        className="flex flex-1 items-center justify-center bg-[#FBF9F4] px-6 py-12 md:w-1/2 md:px-10 md:py-14 lg:px-16"
        data-testid={`${testId}-form`}
      >
        <div className="w-full max-w-md">{children}</div>
      </main>
    </div>
  );
}

/** Rich topographic contour pattern for the brand panel background.
 * Hand-crafted organic SVG paths mimic a real topographic map (matches the mockup).
 * Coloured gold on transparent so it layers over any dark background. */
export function TopoPattern({ className }) {
  return (
    <svg
      className={cn("pointer-events-none absolute inset-0 h-full w-full text-[#B79148]/35", className)}
      viewBox="0 0 800 900"
      preserveAspectRatio="xMidYMid slice"
      fill="none"
      aria-hidden="true"
    >
      <g stroke="currentColor" strokeWidth="1" strokeLinecap="round">
        {/* Upper-right cluster — main topographic mountain */}
        <path d="M 480 40 C 570 55, 660 95, 720 170 C 780 245, 795 335, 745 415 C 695 495, 610 545, 520 550 C 430 555, 355 515, 315 445 C 275 375, 285 285, 335 215 C 385 145, 460 90, 480 40 Z" />
        <path d="M 500 90 C 580 105, 650 145, 700 210 C 750 275, 760 355, 720 425 C 680 495, 605 535, 525 535 C 445 535, 380 500, 350 440 C 320 380, 330 305, 375 245 C 420 185, 480 130, 500 90 Z" />
        <path d="M 520 140 C 585 155, 645 190, 685 245 C 725 300, 730 365, 700 425 C 670 485, 610 515, 540 515 C 470 515, 415 485, 390 435 C 365 385, 375 320, 410 270 C 445 220, 500 175, 520 140 Z" />
        <path d="M 540 190 C 595 205, 645 235, 675 280 C 705 325, 705 375, 685 420 C 665 465, 620 490, 560 490 C 500 490, 455 470, 435 435 C 415 400, 425 350, 450 310 C 475 270, 520 225, 540 190 Z" />
        <path d="M 555 235 C 600 245, 640 270, 660 305 C 680 340, 680 380, 665 415 C 650 450, 620 470, 575 470 C 530 470, 495 455, 480 430 C 465 405, 470 370, 490 340 C 510 310, 545 275, 555 235 Z" />
        <path d="M 570 280 C 605 290, 630 310, 645 335 C 660 360, 660 385, 650 410 C 640 435, 620 450, 590 450 C 560 450, 535 440, 525 420 C 515 400, 520 375, 535 355 C 550 335, 570 310, 570 280 Z" />
        <path d="M 585 325 C 610 335, 625 350, 635 370 C 645 390, 640 410, 630 425 C 620 440, 605 450, 585 450 C 565 450, 555 440, 550 425 C 545 410, 550 390, 560 375 C 570 360, 585 345, 585 325 Z" />

        {/* Lower-left cluster */}
        <path d="M -50 620 C 30 605, 120 620, 195 665 C 270 710, 320 780, 330 855 C 340 930, 305 900, 260 900 L 40 900 C -25 900, -70 875, -85 830 C -100 785, -90 735, -60 690 C -30 645, -50 620, -50 620 Z" />
        <path d="M -30 660 C 45 650, 125 665, 190 705 C 255 745, 295 805, 305 865 C 315 900, 285 900, 250 900 L 60 900 C 5 900, -35 880, -50 845 C -65 810, -55 770, -30 730 C -5 690, -30 660, -30 660 Z" />
        <path d="M -10 705 C 55 695, 125 710, 180 745 C 235 780, 265 830, 275 875 C 285 900, 260 900, 235 900 L 80 900 C 30 900, -5 885, -20 855 C -35 825, -25 795, -5 765 C 15 735, -10 705, -10 705 Z" />
        <path d="M 20 755 C 75 745, 130 760, 175 790 C 220 820, 245 860, 255 890 C 260 900, 245 900, 225 900 L 100 900 C 60 900, 30 890, 20 870 C 10 850, 15 830, 30 810 C 45 790, 20 755, 20 755 Z" />

        {/* Middle wavy contours crossing the panel */}
        <path d="M 0 380 C 100 360, 200 400, 300 380 C 400 360, 500 420, 600 410 C 700 400, 800 430, 900 420" />
        <path d="M 0 420 C 100 400, 200 440, 300 420 C 400 400, 500 460, 600 450 C 700 440, 800 470, 900 460" />
        <path d="M 0 460 C 100 440, 200 480, 300 460 C 400 440, 500 500, 600 490 C 700 480, 800 510, 900 500" />
        <path d="M 0 500 C 120 480, 240 520, 360 500 C 480 480, 600 540, 720 530 C 810 522, 900 540, 900 540" />

        {/* Fine dot grid overlay column */}
      </g>
      <g fill="currentColor" opacity="0.5">
        {Array.from({ length: 22 }).map((_, r) =>
          Array.from({ length: 8 }).map((_, c) => (
            <circle key={`${r}-${c}`} cx={640 + c * 20} cy={40 + r * 32} r="1.2" />
          ))
        )}
      </g>
    </svg>
  );
}

/** Warm accent glow at the bottom-right of the brand panel. */
export function BrandGlow() {
  return (
    <div
      className="pointer-events-none absolute -bottom-24 -right-16 h-[520px] w-[520px] rounded-full opacity-45 blur-3xl"
      style={{ background: "radial-gradient(circle, #C9762A 0%, transparent 62%)" }}
      aria-hidden="true"
    />
  );
}
